import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/dashboard/Layout';
import InventoryOverview from '../components/InventoryOverview';
import BulkImportModal from '../components/BulkImportModal';
import { assetsAPI, labelsAPI } from '../services/api';
import { Laptop, Plus, Search, Printer, Download, Eye, EyeOff, Upload } from 'lucide-react';

const AssetsPage = () => {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [labelData, setLabelData] = useState(null);
  const [showInventoryOverview, setShowInventoryOverview] = useState(true);
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'rental-ready'
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  
  const [formData, setFormData] = useState({
    category: 'laptop',
    inventory_number: '',
    brand: '',
    model: '',
    processor: '',
    ram: '',
    storage: '',
    screen_size: '',
    resolution: '',
    connectivity: '',
    power_output: '',
    serial_number: '',
    purchase_date: new Date().toISOString().split('T')[0],
    purchase_price: '',
    current_value: '',
    daily_rate: '',
    weekly_rate: '',
    monthly_rate: '',
    rental_category: 'company',
    status: 'ready',
    condition_notes: ''
  });
  
  // Custom field states
  const [customFields, setCustomFields] = useState({
    ram: false,
    storage: false,
    screen_size: false,
    resolution: false,
    connectivity: false,
    processor: false,
    power_output: false
  });

  const assetCategories = [
    { value: 'laptop', label: 'Laptop', icon: '💻' },
    { value: 'desktop', label: 'Desktop PC', icon: '🖥️' },
    { value: 'all_in_one', label: 'All-in-One PC', icon: '🖥️' },
    { value: 'mini_pc', label: 'Mini PC', icon: '⬛' },
    { value: 'macbook', label: 'MacBook', icon: '💻' },
    { value: 'imac', label: 'iMac', icon: '🖥️' },
    { value: 'printer', label: 'Printer', icon: '🖨️' },
    { value: 'scanner', label: 'Scanner', icon: '📠' },
    { value: 'photocopy', label: 'Mesin Fotocopy', icon: '📋' },
    { value: 'webcam', label: 'Webcam', icon: '📷' },
    { value: 'camera', label: 'Camera', icon: '📸' },
    { value: 'headset', label: 'Headset', icon: '🎧' },
    { value: 'speaker', label: 'Speaker', icon: '🔊' },
    { value: 'charger', label: 'Charger/Adaptor', icon: '🔌' },
    { value: 'tv', label: 'TV', icon: '📺' },
    { value: 'led_display', label: 'LED Display', icon: '📺' },
    { value: 'projector', label: 'Projector', icon: '📽️' },
    { value: 'multimedia', label: 'Multimedia Lainnya', icon: '🎬' }
  ];

  const needsComputerSpecs = ['laptop', 'desktop', 'all_in_one', 'mini_pc', 'macbook', 'imac'];
  const needsDisplaySpecs = ['tv', 'led_display', 'projector'];
  const needsAudioSpecs = ['speaker', 'headset'];

  useEffect(() => {
    loadAssets();
  }, []);

  const loadAssets = async () => {
    try {
      const response = await assetsAPI.getAll();
      setAssets(response.data);
    } catch (error) {
      console.error('Failed to load assets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (asset = null, e = null) => {
    // Prevent navigation when opening modal
    if (e) {
      e.stopPropagation();
    }
    
    if (asset) {
      // Edit mode
      setEditMode(true);
      setSelectedAsset(asset);
      setFormData({
        category: asset.category || 'laptop',
        inventory_number: asset.inventory_number || '',
        brand: asset.brand,
        model: asset.model,
        processor: asset.processor || '',
        ram: asset.ram || '',
        storage: asset.storage || '',
        screen_size: asset.screen_size || '',
        resolution: asset.resolution || '',
        connectivity: asset.connectivity || '',
        power_output: asset.power_output || '',
        serial_number: asset.serial_number,
        purchase_date: asset.purchase_date.split('T')[0],
        purchase_price: asset.purchase_price,
        current_value: asset.current_value,
        daily_rate: asset.daily_rate || '',
        weekly_rate: asset.weekly_rate || '',
        monthly_rate: asset.monthly_rate || '',
        rental_category: asset.rental_category || 'company',
        status: asset.status,
        condition_notes: asset.condition_notes || ''
      });
    } else {
      // Add mode
      setEditMode(false);
      setSelectedAsset(null);
      setFormData({
        category: 'laptop',
        inventory_number: '',
        brand: '',
        model: '',
        processor: '',
        ram: '',
        storage: '',
        screen_size: '',
        resolution: '',
        connectivity: '',
        power_output: '',
        serial_number: '',
        purchase_date: new Date().toISOString().split('T')[0],
        purchase_price: '',
        current_value: '',
        daily_rate: '',
        weekly_rate: '',
        monthly_rate: '',
        rental_category: 'company',
        status: 'ready',
        condition_notes: ''
      });
      // Reset custom fields
      setCustomFields({
        ram: false,
        storage: false,
        screen_size: false,
        resolution: false,
        connectivity: false,
        processor: false,
        power_output: false
      });
    }
    setShowModal(true);
  };

  const handleCardClick = (asset) => {
    navigate(`/assets/${asset.id}`);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedAsset(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = {
        ...formData,
        purchase_date: new Date(formData.purchase_date).toISOString(),
        purchase_price: parseFloat(formData.purchase_price),
        current_value: parseFloat(formData.current_value)
      };

      let assetId;
      if (editMode) {
        await assetsAPI.update(selectedAsset.id, submitData);
        assetId = selectedAsset.id;
        handleCloseModal();
        loadAssets();
        alert('Asset berhasil diupdate!');
      } else {
        const response = await assetsAPI.create(submitData);
        assetId = response.data.asset_id;
        handleCloseModal();
        loadAssets();
        
        // Generate and show barcode label for new asset
        if (assetId) {
          setTimeout(() => {
            showBarcodeLabel(assetId);
          }, 500);
        }
      }
    } catch (error) {
      alert('Gagal menyimpan asset: ' + (error.response?.data?.detail || error.message));
    }
  };

  const showBarcodeLabel = async (assetId) => {
    try {
      const response = await labelsAPI.getLabel(assetId);
      setLabelData(response.data);
      setShowLabelModal(true);
    } catch (error) {
      console.error('Failed to generate label:', error);
    }
  };

  const handleBulkImportSuccess = () => {
    loadAssets(); // Reload assets after successful import
    setShowBulkImportModal(false);
  };

  const printLabel = () => {
    if (!labelData) return;
    
    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(generatePrintHTML(labelData));
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const generatePrintHTML = (label) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          @page { size: 10cm 6cm; margin: 0; }
          body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
          .label {
            width: 10cm; height: 6cm; padding: 0.5cm;
            box-sizing: border-box; border: 2px solid #333;
          }
          .header { display: flex; justify-content: space-between; margin-bottom: 0.3cm; }
          .qr-code { width: 2cm; height: 2cm; }
          .qr-code img { width: 100%; height: 100%; }
          .info { flex: 1; margin-left: 0.3cm; }
          .brand-model { font-size: 14pt; font-weight: bold; margin-bottom: 0.1cm; }
          .serial { font-size: 10pt; color: #666; margin-bottom: 0.2cm; }
          .specs { font-size: 9pt; line-height: 1.3; }
          .barcode-section { text-align: center; margin-top: 0.3cm; }
          .barcode-section img { width: 8cm; height: auto; }
          .footer { text-align: right; font-size: 8pt; color: #999; margin-top: 0.2cm; }
        </style>
      </head>
      <body>
        <div class="label">
          <div class="header">
            <div class="qr-code"><img src="${label.qr_code}" /></div>
            <div class="info">
              <div class="brand-model">${label.brand} ${label.model}</div>
              <div class="serial">SN: ${label.serial_number}</div>
              <div class="specs">${label.specs}</div>
            </div>
          </div>
          <div class="barcode-section">
            <img src="${label.barcode_image}" />
          </div>
          <div class="footer">ID: ${label.asset_id.substr(0, 8)}</div>
        </div>
      </body>
      </html>
    `;
  };

  // Filter berdasarkan view mode dan search term
  const filteredAssets = assets.filter(asset => {
    // Search filter - tambahkan lebih banyak field untuk search
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || (
      asset.brand?.toLowerCase().includes(searchLower) ||
      asset.model?.toLowerCase().includes(searchLower) ||
      asset.barcode?.toLowerCase().includes(searchLower) ||
      asset.serial_number?.toLowerCase().includes(searchLower) ||
      asset.inventory_number?.toLowerCase().includes(searchLower) ||
      asset.category?.toLowerCase().includes(searchLower)
    );
    
    if (!matchesSearch) return false;
    
    // View mode filter
    if (viewMode === 'rental-ready') {
      // Hanya tampilkan asset yang bisa dirental (exclude: maintenance, service, repair, retired, sold)
      const excludeStatuses = ['maintenance', 'service', 'repair', 'retired', 'sold'];
      return !excludeStatuses.includes(asset.status);
    }
    
    return true;
  });
  
  // Stats untuk rental-ready assets
  const rentalReadyCount = assets.filter(asset => {
    const excludeStatuses = ['maintenance', 'service', 'repair', 'retired', 'sold'];
    return !excludeStatuses.includes(asset.status);
  }).length;

  const getStatusColor = (status) => {
    const colors = {
      ready: 'bg-green-500/10 text-green-600 border-green-500/30',
      rented: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
      on_duty: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
      maintenance: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30',
      service: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
      repair: 'bg-red-500/10 text-red-600 border-red-500/30',
      retired: 'bg-gray-500/10 text-gray-600 border-gray-500/30'
    };
    return colors[status] || colors.ready;
  };

  const getStatusLabel = (status) => {
    const labels = {
      ready: '✅ Ready',
      rented: '🔄 Rented',
      on_duty: '💼 On Duty',
      maintenance: '🔧 Maintenance',
      service: '⚠️ Service',
      repair: '🛠️ Repair',
      retired: '🚫 Retired'
    };
    return labels[status] || status;
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">Asset Management</h1>
            <p className="text-muted-foreground">Manage inventory dan tracking lengkap</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowInventoryOverview(!showInventoryOverview)}
              className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors flex items-center space-x-2"
            >
              {showInventoryOverview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              <span>{showInventoryOverview ? 'Hide' : 'Show'} Overview</span>
            </button>
            <button
              onClick={() => setShowBulkImportModal(true)}
              className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 transition-colors flex items-center space-x-2"
              data-testid="import-assets-button"
            >
              <Upload className="w-4 h-4" />
              <span>Import Assets</span>
            </button>
            <button
              onClick={() => handleOpenModal()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center space-x-2 btn"
              data-testid="add-asset-button"
            >
              <Plus className="w-4 h-4" />
              <span>Add Asset</span>
            </button>
          </div>
        </div>

        {/* Inventory Overview */}
        {showInventoryOverview && <InventoryOverview assets={assets} />}

        {/* View Mode Toggle */}
        <div className="glass-card p-4 rounded-xl border border-border/40">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-muted-foreground">Tampilan:</span>
              <div className="flex space-x-2">
                <button
                  onClick={() => setViewMode('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    viewMode === 'all'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground hover:bg-muted/80'
                  }`}
                >
                  📦 Semua Asset ({assets.length})
                </button>
                <button
                  onClick={() => setViewMode('rental-ready')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    viewMode === 'rental-ready'
                      ? 'bg-secondary text-secondary-foreground'
                      : 'bg-muted text-foreground hover:bg-muted/80'
                  }`}
                >
                  🚀 Asset Siap Rental ({rentalReadyCount})
                </button>
              </div>
            </div>
            
            {viewMode === 'rental-ready' && (
              <div className="text-sm text-muted-foreground bg-secondary/10 px-3 py-1.5 rounded-lg border border-secondary/30">
                💡 Menampilkan asset yang siap dirental (exclude: maintenance, service, repair, retired, sold)
              </div>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder={viewMode === 'rental-ready' ? 'Cari asset siap rental (brand/model/barcode)...' : 'Search by brand, model, or barcode...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
              data-testid="search-assets"
            />
          </div>
          
          {/* Search Results Counter */}
          {searchTerm && (
            <div className="flex items-center justify-between px-2">
              <p className="text-sm text-muted-foreground">
                {filteredAssets.length > 0 ? (
                  <>
                    🔍 Ditemukan <span className="font-bold text-primary">{filteredAssets.length}</span> asset{filteredAssets.length > 1 ? 's' : ''} yang cocok dengan "{searchTerm}"
                  </>
                ) : (
                  <>
                    ❌ Tidak ada asset yang cocok dengan "{searchTerm}"
                  </>
                )}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-sm text-primary hover:text-primary/80 font-medium"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>

        {/* Assets Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading assets...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredAssets.map((asset) => (
              <div
                key={asset.id}
                onClick={() => handleCardClick(asset)}
                className="glass-card p-6 rounded-xl border border-border/40 hover:border-primary/30 transition-all cursor-pointer"
                data-testid="asset-card"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-1">
                      {asset.brand} {asset.model}
                    </h3>
                    <p className="text-sm text-muted-foreground mono">{asset.barcode}</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusColor(asset.status)}`}>
                    {getStatusLabel(asset.status)}
                  </span>
                </div>

                <div className="space-y-2 text-sm">
                  {viewMode === 'rental-ready' && (
                    <>
                      {/* Availability Status & Location */}
                      <div className="flex items-center justify-between mb-3 p-2 bg-secondary/10 rounded-lg border border-secondary/30">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-medium text-secondary">📍 Lokasi:</span>
                          <span className="text-xs text-foreground">{asset.current_location || 'Warehouse'}</span>
                        </div>
                      </div>
                      
                      {/* Rental Rates */}
                      <div className="space-y-1.5 p-3 bg-primary/5 rounded-lg border border-primary/20">
                        <p className="text-xs font-semibold text-primary mb-2">💰 Harga Rental:</p>
                        <div className="flex justify-between">
                          <span className="text-xs text-muted-foreground">Harian:</span>
                          <span className="text-xs text-foreground font-medium mono">
                            Rp {(asset.daily_rate || 0).toLocaleString('id-ID')}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs text-muted-foreground">Mingguan:</span>
                          <span className="text-xs text-foreground font-medium mono">
                            Rp {(asset.weekly_rate || 0).toLocaleString('id-ID')}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs text-muted-foreground">Bulanan:</span>
                          <span className="text-xs text-foreground font-medium mono">
                            Rp {(asset.monthly_rate || 0).toLocaleString('id-ID')}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Specs - Always show */}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Processor:</span>
                    <span className="text-foreground font-medium">{asset.processor}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">RAM:</span>
                    <span className="text-foreground font-medium">{asset.ram}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Storage:</span>
                    <span className="text-foreground font-medium">{asset.storage}</span>
                  </div>
                  
                  {viewMode !== 'rental-ready' && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Value:</span>
                      <span className="text-foreground font-medium mono">
                        Rp {(asset.current_value / 1000000).toFixed(1)}M
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && filteredAssets.length === 0 && (
          <div className="glass-card p-12 rounded-xl border border-border/40 text-center">
            <Laptop className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            {searchTerm ? (
              <>
                <h3 className="text-lg font-semibold text-foreground mb-2">Asset Tidak Ditemukan</h3>
                <p className="text-muted-foreground mb-4">
                  Tidak ada asset yang cocok dengan pencarian "<span className="font-bold">{searchTerm}</span>"
                </p>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>💡 Tips pencarian:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Coba gunakan kata kunci yang lebih umum (contoh: "Dell" bukan "Dell XPS")</li>
                    <li>Pastikan ejaan brand/model sudah benar</li>
                    <li>Coba cari dengan barcode lengkap</li>
                    {viewMode === 'rental-ready' && <li>Atau switch ke "Semua Asset" untuk melihat semua inventory</li>}
                  </ul>
                </div>
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Clear Search
                </button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-foreground mb-2">Belum Ada Asset</h3>
                <p className="text-muted-foreground mb-4">
                  {viewMode === 'rental-ready' 
                    ? 'Tidak ada asset yang siap dirental saat ini'
                    : 'Belum ada asset dalam database'
                  }
                </p>
                <button
                  onClick={() => handleOpenModal()}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center space-x-2 mx-auto"
                >
                  <Plus className="w-4 h-4" />
                  <span>Tambah Asset Pertama</span>
                </button>
              </>
            )}
          </div>
        )}

        {/* Barcode Label Modal */}
        {showLabelModal && labelData && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="glass-card p-6 rounded-xl border border-border/40 max-w-3xl w-full">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-foreground">Barcode Label Asset</h2>
                <button
                  onClick={() => setShowLabelModal(false)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  ✕
                </button>
              </div>

              {/* Label Preview */}
              <div className="bg-white p-8 rounded-lg mb-6">
                <div className="border-2 border-gray-800 p-6" style={{width: '10cm', height: '6cm', margin: '0 auto'}}>
                  <div className="flex justify-between mb-4">
                    <div style={{width: '2cm', height: '2cm'}}>
                      <img src={labelData.qr_code} alt="QR Code" className="w-full h-full" />
                    </div>
                    <div className="flex-1 ml-4">
                      <div className="text-lg font-bold text-gray-900 mb-1">
                        {labelData.brand} {labelData.model}
                      </div>
                      <div className="text-sm text-gray-600 mb-2">
                        SN: {labelData.serial_number}
                      </div>
                      <div className="text-xs text-gray-700">
                        {labelData.specs}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center mt-4">
                    <img 
                      src={labelData.barcode_image} 
                      alt="Barcode" 
                      style={{width: '8cm', margin: '0 auto'}}
                    />
                  </div>
                  
                  <div className="text-right text-xs text-gray-500 mt-2">
                    Asset ID: {labelData.asset_id.substr(0, 8)}
                  </div>
                </div>
              </div>

              {/* Asset Info */}
              <div className="glass-card p-4 rounded-lg mb-6 border border-border/40">
                <h3 className="text-sm font-semibold text-foreground mb-3">Detail Asset</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Barcode:</span>
                    <span className="text-foreground font-mono ml-2">{labelData.barcode_text}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Screen:</span>
                    <span className="text-foreground ml-2">{labelData.screen_size}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status:</span>
                    <span className="text-foreground ml-2 capitalize">{labelData.status}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Purchase Date:</span>
                    <span className="text-foreground ml-2">{new Date(labelData.purchase_date).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={printLabel}
                  className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center space-x-2 btn"
                >
                  <Printer className="w-4 h-4" />
                  <span>Print Label</span>
                </button>
                <button
                  onClick={() => setShowLabelModal(false)}
                  className="px-6 py-3 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                >
                  Tutup
                </button>
              </div>

              <div className="mt-4 text-xs text-muted-foreground text-center">
                💡 Label ini siap dicetak. Gunakan ukuran kertas 10cm x 6cm atau sesuaikan di printer settings.
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Asset Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
            <div className="glass-card p-4 sm:p-6 rounded-xl border border-border/40 max-w-full sm:max-w-2xl lg:max-w-4xl w-full my-4 sm:my-8 max-h-[95vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-foreground mb-6">
                {editMode ? 'Edit Asset' : 'Tambah Asset Baru'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Category Selection */}
                <div>
                  <label className="block text-sm font-medium text-foreground/80 mb-2">
                    Kategori Asset <span className="text-destructive">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                    required
                  >
                    {assetCategories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.icon} {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Brand */}
                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-2">
                      Brand <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.brand}
                      onChange={(e) => setFormData({...formData, brand: e.target.value})}
                      className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                      placeholder="Dell, HP, Lenovo, dll"
                      required
                    />
                  </div>

                  {/* Model */}
                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-2">
                      Model <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.model}
                      onChange={(e) => setFormData({...formData, model: e.target.value})}
                      className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                      placeholder="Latitude 5420, ThinkPad X1"
                      required
                    />
                  </div>

                  {/* Computer Specs (Laptop, Desktop, etc) */}
                  {needsComputerSpecs.includes(formData.category) && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-foreground/80 mb-2">
                          Processor <span className="text-destructive">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.processor}
                          onChange={(e) => setFormData({...formData, processor: e.target.value})}
                          className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                          placeholder="Intel Core i5 12th Gen / M1 / M2"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground/80 mb-2">
                          RAM <span className="text-destructive">*</span>
                        </label>
                        <select
                          value={customFields.ram ? 'CUSTOM' : formData.ram}
                          onChange={(e) => {
                            if (e.target.value === 'CUSTOM') {
                              setCustomFields({...customFields, ram: true});
                              setFormData({...formData, ram: ''});
                            } else {
                              setCustomFields({...customFields, ram: false});
                              setFormData({...formData, ram: e.target.value});
                            }
                          }}
                          className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                          required={!customFields.ram}
                        >
                          <option value="">Pilih RAM</option>
                          <option value="4GB">4GB</option>
                          <option value="8GB DDR4">8GB DDR4</option>
                          <option value="16GB DDR4">16GB DDR4</option>
                          <option value="32GB DDR4">32GB DDR4</option>
                          <option value="64GB DDR4">64GB DDR4</option>
                          <option value="8GB Unified">8GB Unified (Mac)</option>
                          <option value="16GB Unified">16GB Unified (Mac)</option>
                          <option value="CUSTOM">✏️ Custom (Isi Manual)</option>
                        </select>
                        {customFields.ram && (
                          <input
                            type="text"
                            value={formData.ram}
                            onChange={(e) => setFormData({...formData, ram: e.target.value})}
                            className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors mt-2"
                            placeholder="Masukkan RAM custom, contoh: 128GB DDR5"
                            required
                          />
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground/80 mb-2">
                          Storage <span className="text-destructive">*</span>
                        </label>
                        <select
                          value={customFields.storage ? 'CUSTOM' : formData.storage}
                          onChange={(e) => {
                            if (e.target.value === 'CUSTOM') {
                              setCustomFields({...customFields, storage: true});
                              setFormData({...formData, storage: ''});
                            } else {
                              setCustomFields({...customFields, storage: false});
                              setFormData({...formData, storage: e.target.value});
                            }
                          }}
                          className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                          required={!customFields.storage}
                        >
                          <option value="">Pilih Storage</option>
                          <option value="128GB SSD">128GB SSD</option>
                          <option value="256GB SSD">256GB SSD</option>
                          <option value="512GB SSD">512GB SSD</option>
                          <option value="1TB SSD">1TB SSD</option>
                          <option value="2TB SSD">2TB SSD</option>
                          <option value="500GB HDD">500GB HDD</option>
                          <option value="1TB HDD">1TB HDD</option>
                          <option value="CUSTOM">✏️ Custom (Isi Manual)</option>
                        </select>
                        {customFields.storage && (
                          <input
                            type="text"
                            value={formData.storage}
                            onChange={(e) => setFormData({...formData, storage: e.target.value})}
                            className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors mt-2"
                            placeholder="Masukkan Storage custom, contoh: 4TB NVMe"
                            required
                          />
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground/80 mb-2">
                          Screen Size <span className="text-destructive">*</span>
                        </label>
                        <select
                          value={customFields.screen_size ? 'CUSTOM' : formData.screen_size}
                          onChange={(e) => {
                            if (e.target.value === 'CUSTOM') {
                              setCustomFields({...customFields, screen_size: true});
                              setFormData({...formData, screen_size: ''});
                            } else {
                              setCustomFields({...customFields, screen_size: false});
                              setFormData({...formData, screen_size: e.target.value});
                            }
                          }}
                          className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                          required={!customFields.screen_size}
                        >
                          <option value="">Pilih Screen Size</option>
                          <option value="13 inch">13 inch</option>
                          <option value="14 inch">14 inch</option>
                          <option value="15.6 inch">15.6 inch</option>
                          <option value="17 inch">17 inch</option>
                          <option value="21.5 inch">21.5 inch</option>
                          <option value="24 inch">24 inch</option>
                          <option value="27 inch">27 inch</option>
                          <option value="CUSTOM">✏️ Custom (Isi Manual)</option>
                        </select>
                        {customFields.screen_size && (
                          <input
                            type="text"
                            value={formData.screen_size}
                            onChange={(e) => setFormData({...formData, screen_size: e.target.value})}
                            className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors mt-2"
                            placeholder="Masukkan Screen Size custom, contoh: 32 inch"
                            required
                          />
                        )}
                      </div>
                    </>
                  )}

                  {/* Display Specs (TV, LED, Projector) */}
                  {needsDisplaySpecs.includes(formData.category) && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-foreground/80 mb-2">
                          Screen Size <span className="text-destructive">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.screen_size}
                          onChange={(e) => setFormData({...formData, screen_size: e.target.value})}
                          className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                          placeholder="32 inch, 43 inch, 55 inch"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground/80 mb-2">
                          Resolution <span className="text-destructive">*</span>
                        </label>
                        <select
                          value={formData.resolution}
                          onChange={(e) => setFormData({...formData, resolution: e.target.value})}
                          className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                          required
                        >
                          <option value="">Pilih Resolution</option>
                          <option value="HD 720p">HD 720p</option>
                          <option value="Full HD 1080p">Full HD 1080p</option>
                          <option value="2K QHD">2K QHD</option>
                          <option value="4K UHD">4K UHD</option>
                          <option value="8K">8K</option>
                        </select>
                      </div>
                    </>
                  )}

                  {/* Audio Specs (Speaker, Headset) */}
                  {needsAudioSpecs.includes(formData.category) && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-foreground/80 mb-2">
                          Power Output
                        </label>
                        <input
                          type="text"
                          value={formData.power_output}
                          onChange={(e) => setFormData({...formData, power_output: e.target.value})}
                          className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                          placeholder="20W, 50W RMS, dll"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground/80 mb-2">
                          Connectivity
                        </label>
                        <input
                          type="text"
                          value={formData.connectivity}
                          onChange={(e) => setFormData({...formData, connectivity: e.target.value})}
                          className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                          placeholder="Bluetooth, USB, 3.5mm Jack"
                        />
                      </div>
                    </>
                  )}

                  {/* General Connectivity for other devices */}
                  {!needsComputerSpecs.includes(formData.category) && !needsDisplaySpecs.includes(formData.category) && !needsAudioSpecs.includes(formData.category) && (
                    <div>
                      <label className="block text-sm font-medium text-foreground/80 mb-2">
                        Connectivity/Interface
                      </label>
                      <input
                        type="text"
                        value={formData.connectivity}
                        onChange={(e) => setFormData({...formData, connectivity: e.target.value})}
                        className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                        placeholder="USB, Wireless, HDMI, dll"
                      />
                    </div>
                  )}

                  {/* Inventory Number */}
                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-2">
                      Nomor Inventory Perusahaan
                    </label>
                    <input
                      type="text"
                      value={formData.inventory_number}
                      onChange={(e) => setFormData({...formData, inventory_number: e.target.value})}
                      className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                      placeholder="INV-2024-001 (Opsional)"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Nomor inventory khusus dari perusahaan Anda (opsional)
                    </p>
                  </div>

                  {/* Serial Number */}
                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-2">
                      Serial Number <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.serial_number}
                      onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
                      className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                      placeholder="SN1234567890"
                      required
                    />
                  </div>

                  {/* Purchase Date */}
                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-2">
                      Purchase Date <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.purchase_date}
                      onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
                      className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                      required
                    />
                  </div>

                  {/* Purchase Price */}
                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-2">
                      Purchase Price (Rp) <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.purchase_price}
                      onChange={(e) => setFormData({...formData, purchase_price: e.target.value})}
                      className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                      placeholder="8000000"
                      required
                    />
                  </div>

                  {/* Current Value */}
                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-2">
                      Current Value (Rp) <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.current_value}
                      onChange={(e) => setFormData({...formData, current_value: e.target.value})}
                      className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                      placeholder="6000000"
                      required
                    />
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-2">
                      Status <span className="text-destructive">*</span>
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                      required
                    >
                      <option value="ready">✅ Ready (Siap Disewa)</option>
                      <option value="rented">🔄 Rented (Sedang Disewa)</option>
                      <option value="on_duty">💼 On Duty (Digunakan Internal/Demo)</option>
                      <option value="maintenance">🔧 Maintenance (Perawatan Rutin)</option>
                      <option value="service">⚠️ Service (Perlu Perbaikan)</option>
                      <option value="repair">🛠️ Repair (Dalam Perbaikan)</option>
                      <option value="retired">🚫 Retired (Tidak Layak Pakai)</option>
                    </select>
                  </div>
                </div>

                {/* Rental Pricing Section */}
                <div className="border-t border-border pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">💰 Harga Sewa (Rental)</h3>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                    {/* Daily Rate */}
                    <div>
                      <label className="block text-sm font-medium text-foreground/80 mb-2">
                        Harga Harian (Rp)
                      </label>
                      <input
                        type="number"
                        value={formData.daily_rate}
                        onChange={(e) => setFormData({...formData, daily_rate: e.target.value})}
                        className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                        placeholder="0"
                        min="0"
                      />
                    </div>

                    {/* Weekly Rate */}
                    <div>
                      <label className="block text-sm font-medium text-foreground/80 mb-2">
                        Harga Mingguan (Rp)
                      </label>
                      <input
                        type="number"
                        value={formData.weekly_rate}
                        onChange={(e) => setFormData({...formData, weekly_rate: e.target.value})}
                        className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                        placeholder="0"
                        min="0"
                      />
                    </div>

                    {/* Monthly Rate */}
                    <div>
                      <label className="block text-sm font-medium text-foreground/80 mb-2">
                        Harga Bulanan (Rp)
                      </label>
                      <input
                        type="number"
                        value={formData.monthly_rate}
                        onChange={(e) => setFormData({...formData, monthly_rate: e.target.value})}
                        className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  </div>

                  {/* Rental Category */}
                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-2">
                      Kategori Rental
                    </label>
                    <select
                      value={formData.rental_category}
                      onChange={(e) => setFormData({...formData, rental_category: e.target.value})}
                      className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                    >
                      <option value="company">🏢 Rental Perusahaan</option>
                      <option value="individual">👤 Rental Perorangan</option>
                      <option value="event">🎉 Rental Event</option>
                    </select>
                  </div>
                </div>

                {/* Condition Notes */}
                <div>
                  <label className="block text-sm font-medium text-foreground/80 mb-2">
                    Condition Notes (Opsional)
                  </label>
                  <textarea
                    value={formData.condition_notes}
                    onChange={(e) => setFormData({...formData, condition_notes: e.target.value})}
                    className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors resize-none"
                    rows="3"
                    placeholder="Catatan kondisi laptop, kerusakan, dll"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 px-4 py-3 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors btn"
                  >
                    {editMode ? 'Update Asset' : 'Tambah Asset'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Bulk Import Modal */}
        {showBulkImportModal && (
          <BulkImportModal
            isOpen={showBulkImportModal}
            onClose={() => setShowBulkImportModal(false)}
            onSuccess={handleBulkImportSuccess}
          />
        )}
      </div>
    </Layout>
  );
};

export default AssetsPage;
